import { useRef, useState } from "react";
import { PopOutButton } from "@/components/dashboard/PopOut";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ReferenceLine, ReferenceDot } from "recharts";
import { X } from "@phosphor-icons/react";
import { money, fmtDate } from "@/lib/api";
import { resample, zeroCashPoint, withLastWeek } from "@/lib/chartRange";
import { CompareToggle, ExportChartButton, useLastWeek } from "@/components/dashboard/ChartTools";
import { ChangeSummary } from "@/components/dashboard/ChangeSummary";
import { TargetGap, TargetPicker, useTarget } from "@/components/dashboard/TargetDate";
import { RangePicker, useChartRange } from "@/components/dashboard/RangePicker";

const TipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[#2A2A2A] bg-[#050505]/95 px-3 py-2 font-mono text-xs">
      <p className="text-[#A3A3A3] mb-1">{label}</p>
      {payload.filter((p) => p.value != null).map((p) => (
        <p key={p.dataKey} style={{ color: p.stroke }}>{p.name}: {money(p.value)}</p>
      ))}
    </div>
  );
};

const Legend = ({ color, label, dashed }) => (
  <span className="flex items-center gap-2 text-xs text-[#A3A3A3]">
    <span className="w-5 h-0.5" style={{ background: dashed ? `repeating-linear-gradient(90deg, ${color} 0 4px, transparent 4px 7px)` : color }} />{label}
  </span>
);

const TICK_PX = 64;

export const RunwayChart = ({ series: full, lines = [], onClear, zeroCashDate }) => {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  const rp = useChartRange();
  const lw = useLastWeek();
  const series = resample(lw.on ? withLastWeek(full, lw.snapshot) : full, rp.range);
  const zero = zeroCashPoint(series, zeroCashDate);
  const target = useTarget();
  const targetPt = zeroCashPoint(series, target?.target);
  const hasPipe = series.some((r) => r.pipeline != null);
  const targetOff = target?.target && !targetPt;
  const nowLabel = series.find((r) => r.actual != null && r.forecast != null)?.month;
  const interval = width ? Math.max(0, Math.ceil(series.length / Math.max(2, Math.floor((width - 92) / TICK_PX))) - 1) : 2;
  return (
  <div data-testid="runway-chart" ref={ref} className="rounded-xl border border-[#2A2A2A] bg-[#121212] p-6">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap gap-5">
        <Legend color="#00FF9D" label="Actual cash" />
        <Legend color="#00FF9D99" label="Baseline forecast" dashed />
        {hasPipe && <Legend color="#FF7AC6" label="Runway incl. pipeline" dashed />}
        {lines.map((l) => <Legend key={l.key} color={l.color} label={l.label} dashed />)}
        {lw.on && <Legend color="#A3A3A3" label={`Forecast at ${fmtDate(lw.snapshot.taken_at)} digest`} dashed />}
      </div>
      <div className="flex flex-wrap items-center gap-2" data-export-ignore="1">
        <RangePicker {...rp} />
        <CompareToggle {...lw} />
        <TargetPicker target={target} />
        <ExportChartButton target={ref} range={rp.range} />
        <PopOutButton />
        {lines.length > 0 && (
        <button data-testid="clear-scenario-btn" onClick={onClear}
          className="flex items-center gap-1 rounded-full border border-[#9D00FF]/50 px-3 py-1 text-xs text-[#D9A6FF] hover:bg-[#9D00FF]/20 transition-colors">
          <X size={12} /> Clear scenarios
        </button>
        )}
      </div>
    </div>
    <div className="chart-area h-[340px] mt-6">
      <ResponsiveContainer width="100%" height="100%" onResize={(w) => setWidth(w)}>
        <ComposedChart data={series} margin={{ top: 10, right: 28, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="cashFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00FF9D" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#00FF9D" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="month" tick={{ fill: "#6B6B6B", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} interval={interval} tickMargin={8} padding={{ left: 10 }} />
          <YAxis tickFormatter={(v) => money(v, true)} tick={{ fill: "#6B6B6B", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={64} />
          <Tooltip content={<TipBox />} cursor={{ stroke: "#2A2A2A" }} />
          <ReferenceLine y={0} stroke="#FF5C7A" strokeOpacity={0.5} strokeDasharray="2 4" />
          {nowLabel && <ReferenceLine x={nowLabel} stroke="#A3A3A3" strokeOpacity={0.35} strokeDasharray="3 3"
            label={{ value: "Today", position: "insideTopRight", fill: "#A3A3A3", fontSize: 10, fontFamily: "JetBrains Mono" }} />}
          <Area type="monotone" dataKey="actual" name="Actual" stroke="#00FF9D" strokeWidth={2.5} fill="url(#cashFill)" animationDuration={1400} connectNulls={false}
            dot={series.filter((r) => r.actual != null).length === 1 ? { r: 4, fill: "#00FF9D", stroke: "#00FF9D" } : false} />
          <Line type="monotone" dataKey="forecast" name="Baseline" stroke="#00FF9D" strokeOpacity={0.6} strokeDasharray="5 5" strokeWidth={2} dot={false} animationDuration={1400} />
          {hasPipe && <Line type="monotone" dataKey="pipeline" name="Incl. pipeline" stroke="#FF7AC6" strokeOpacity={0.85} strokeDasharray="1 5" strokeLinecap="round" strokeWidth={2.5} dot={false} animationDuration={1200} />}
          {lw.on && <Line type="monotone" dataKey="lw" name="Last Monday" stroke="#A3A3A3" strokeOpacity={0.8} strokeDasharray="2 4" strokeWidth={2} dot={false} animationDuration={900} />}
          {targetPt && <ReferenceLine x={targetPt.month} stroke="#FFB800" strokeWidth={1.5}
            label={{ value: `Target · ${fmtDate(target.target)}`, position: "insideTopLeft", fill: "#FFD166", fontSize: 11, fontFamily: "JetBrains Mono" }} />}
          {zero && <ReferenceLine x={zero.month} stroke="#FF5C7A" strokeWidth={1.5} strokeDasharray="4 3"
            label={{ value: `Zero cash · ${fmtDate(zeroCashDate)}`, position: "insideBottomLeft", fill: "#FF8FA3", fontSize: 11, fontFamily: "JetBrains Mono" }} />}
          {zero && <ReferenceDot x={zero.month} y={0} r={5} fill="#FF5C7A" stroke="#050505" strokeWidth={2} />}
          {lines.map((l) => (
            <Line key={l.key} type="monotone" dataKey={l.key} name={l.label} stroke={l.color} strokeDasharray="6 4" strokeWidth={2.5} dot={false} animationDuration={1200} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
    {targetOff && <p data-testid="target-offchart-hint" className="mt-2 text-[11px] text-[#FFD166]/80 font-mono">Target {fmtDate(target.target)} is outside this view. Pick a longer range to see it.</p>}
    <TargetGap target={target} />
    <ChangeSummary />
  </div>
  );
};
